/**
 * @file: facadeSiteRequirements.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { InspectionSiteInfo, InspectionTemplateConf } from '@euler/model';
import { isNotNull } from '@euler/utils';

export type FacadeSiteRequirements = {
  /** The sites that must be checked, in the order the template declares them. */
  requiredSiteIds: number[];
  /** The sites whose media must be captured live instead of picked. */
  mediaCaptureRequiredSiteIds: Set<number>;
};

/**
 * Reads the per-site requirements off a facade inspection template.
 *
 * A site may have been dropped from its group - or disabled - since the
 * required lists were configured, in which case it no longer applies.
 */
export const facadeSiteRequirementsOf = (
  conf: InspectionTemplateConf | undefined,
): FacadeSiteRequirements => {
  // `categories` is typed as required, but a template row holding an empty
  // conf parses to `{}` on the way out of the server.
  const groups = conf?.categories?.flatMap(x => x.groups) ?? [];
  const disabledSiteIds = new Set(groups.flatMap(x => x.disabledSiteIds ?? []));

  const requiredSiteIds: number[] = [];
  const mediaCaptureRequiredSiteIds = new Set<number>();

  for (const group of groups) {
    const groupSiteIds = new Set(group.siteIds);
    const applies = (siteId: number) =>
      groupSiteIds.has(siteId) && !disabledSiteIds.has(siteId);

    for (const siteId of group.requiredSiteIds ?? []) {
      if (applies(siteId) && !requiredSiteIds.includes(siteId)) {
        requiredSiteIds.push(siteId);
      }
    }
    for (const siteId of group.mediaCaptureRequiredSiteIds ?? []) {
      if (applies(siteId)) {
        mediaCaptureRequiredSiteIds.add(siteId);
      }
    }
  }

  return { requiredSiteIds, mediaCaptureRequiredSiteIds };
};

/**
 * A facade site carries no result of its own - the item result is derived from
 * the photos it was recorded with - so it counts as checked once it holds at
 * least one media, no matter whether that was shot or picked from the gallery.
 */
export const facadeSiteHasMedia = (
  siteInspection: { items: { medias: unknown[] }[] } | undefined,
) => Boolean(siteInspection?.items.some(x => x.medias.length > 0));

/** Resolves site ids against the inventory, dropping any that no longer exist. */
export const resolveFacadeSites = (
  siteIds: number[],
  getSiteById: (siteId: number) => InspectionSiteInfo | undefined,
): InspectionSiteInfo[] =>
  siteIds
    .map(siteId => {
      const site = getSiteById(siteId);
      if (!site) {
        __DEV__ &&
          console.log(
            `required facade site #${siteId} does not exist any more`,
          );
      }
      return site;
    })
    .filter(isNotNull);

/** The required sites that hold no photo yet, resolved against the inventory. */
export const uncheckedRequiredFacadeSites = (
  requiredSiteIds: number[],
  isChecked: (siteId: number) => boolean,
  getSiteById: (siteId: number) => InspectionSiteInfo | undefined,
): InspectionSiteInfo[] =>
  resolveFacadeSites(
    requiredSiteIds.filter(siteId => !isChecked(siteId)),
    getSiteById,
  );
